import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Alert,
  AlertTitle,
  Grid,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Handshake as HandshakeIcon,
  RateReview as RateReviewIcon,
  RepeatOne as RepeatOneIcon,
  Add as AddIcon,
  Help as HelpIcon
} from '@mui/icons-material';

interface CommunicationTemplatesProps {
  campaignId: string;
  organizationId: string;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  body: string;
  trigger: TemplateTrigger;
  enabled: boolean;
}

type TemplateTrigger = 
  | 'application_submitted' 
  | 'match_confirmed' 
  | 'feedback_requested' 
  | 'weekly_checkin' 
  | 'custom';

const CommunicationTemplatesPanel: React.FC<CommunicationTemplatesProps> = ({
  campaignId,
  organizationId
}) => {
  const [templates, setTemplates] = useState<Template[]>([
    {
      id: '1',
      name: 'Application Confirmation',
      subject: 'We received your application',
      body: 'Dear applicant,\n\nThank you for applying to our mentorship program. We have received your application and will review it shortly.\n\nBest regards,\nMentoringFusion Team',
      trigger: 'application_submitted',
      enabled: true
    },
    {
      id: '2',
      name: 'Match Notification',
      subject: 'You have been matched!',
      body: 'Dear participant,\n\nWe are pleased to inform you that you have been matched with a mentor/mentee. Please log in to your account to view the details.\n\nBest regards,\nMentoringFusion Team',
      trigger: 'match_confirmed',
      enabled: true
    },
    {
      id: '3',
      name: 'Feedback Request',
      subject: 'Please provide feedback on your mentorship experience',
      body: 'Dear participant,\n\nWe would appreciate your feedback on your mentorship experience. Please take a few minutes to complete the survey.\n\nBest regards,\nMentoringFusion Team',
      trigger: 'feedback_requested',
      enabled: true
    },
    {
      id: '4',
      name: 'Weekly Check-in',
      subject: 'Weekly mentorship check-in',
      body: 'Dear participant,\n\nThis is a friendly reminder to check in with your mentor/mentee this week. Regular communication is key to a successful mentorship relationship.\n\nBest regards,\nMentoringFusion Team',
      trigger: 'weekly_checkin',
      enabled: false
    }
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [weeklyFrequency, setWeeklyFrequency] = useState<number>(1);
  const [expanded, setExpanded] = useState<string | false>('template-1');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleTemplateChange = (templateId: string, field: keyof Template, value: any) => {
    setTemplates(templates.map(template => 
      template.id === templateId ? { ...template, [field]: value } : template
    ));
  };

  const handleAddNewTemplate = () => {
    const newId = (Math.max(...templates.map(t => parseInt(t.id))) + 1).toString();
    
    const newTemplate: Template = {
      id: newId,
      name: 'New Template',
      subject: 'Subject line',
      body: 'Email body',
      trigger: 'custom',
      enabled: false
    };
    
    setTemplates([...templates, newTemplate]);
    setExpanded(`template-${newId}`);
  };

  const handleSaveTemplates = () => {
    // In a real app, you would save the templates to your backend here
    console.log("Saving templates:", templates);
    // API call would go here
  };

  const getTriggerLabel = (trigger: TemplateTrigger): string => {
    switch(trigger) {
      case 'application_submitted': return 'When user applies';
      case 'match_confirmed': return 'When user is matched';
      case 'feedback_requested': return 'When feedback is requested';
      case 'weekly_checkin': return 'Weekly check-in';
      case 'custom': return 'Custom trigger';
      default: return 'Unknown trigger';
    }
  };

  const getTriggerIcon = (trigger: TemplateTrigger) => {
    switch(trigger) {
      case 'application_submitted': return <EmailIcon />;
      case 'match_confirmed': return <HandshakeIcon />;
      case 'feedback_requested': return <RateReviewIcon />;
      case 'weekly_checkin': return <RepeatOneIcon />;
      case 'custom': return <HelpIcon />;
      default: return null;
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Communication Templates</Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>About Communication Templates</AlertTitle>
          Configure email templates that will be automatically sent to participants at key moments in your mentorship program.
          Enable or disable templates, customize content, and set sending frequency as needed.
        </Alert>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        {templates.map((template) => (
          <Accordion 
            key={template.id}
            expanded={expanded === `template-${template.id}`}
            onChange={handleAccordionChange(`template-${template.id}`)}
            sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 2, color: 'primary.main' }}>
                    {getTriggerIcon(template.trigger)}
                  </Box>
                  <Typography variant="subtitle1">{template.name}</Typography>
                  <Chip 
                    label={getTriggerLabel(template.trigger)} 
                    size="small" 
                    sx={{ ml: 2 }}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={template.enabled}
                      onChange={(e) => handleTemplateChange(template.id, 'enabled', e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                    />
                  }
                  label={template.enabled ? "Enabled" : "Disabled"}
                  sx={{ mr: 0 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={template.name}
                  onChange={(e) => handleTemplateChange(template.id, 'name', e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Trigger</InputLabel>
                      <Select
                        value={template.trigger}
                        label="Trigger"
                        onChange={(e) => handleTemplateChange(template.id, 'trigger', e.target.value)}
                      >
                        <MenuItem value="application_submitted">When user applies</MenuItem>
                        <MenuItem value="match_confirmed">When user is matched</MenuItem>
                        <MenuItem value="feedback_requested">When feedback is requested</MenuItem>
                        <MenuItem value="weekly_checkin">Weekly check-in</MenuItem>
                        <MenuItem value="custom">Custom trigger</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {template.trigger === 'weekly_checkin' && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Frequency</InputLabel>
                        <Select
                          value={weeklyFrequency}
                          label="Frequency"
                          onChange={(e) => setWeeklyFrequency(Number(e.target.value))}
                        >
                          <MenuItem value={1}>Every week</MenuItem>
                          <MenuItem value={2}>Every 2 weeks</MenuItem>
                          <MenuItem value={4}>Every month</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email Subject"
                  value={template.subject}
                  onChange={(e) => handleTemplateChange(template.id, 'subject', e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Email Body"
                  value={template.body}
                  onChange={(e) => handleTemplateChange(template.id, 'body', e.target.value)}
                  multiline
                  rows={6}
                  variant="outlined"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddNewTemplate}
        >
          Add Template
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveTemplates}
        >
          Save Templates
        </Button>
      </Box>
    </Box>
  );
};

export default CommunicationTemplatesPanel;
